import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import ErrorBoundary from "components/errorBoundary/errorBoundary";
import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";
import type { AboutPageData } from "content-ui/utilityPages/about/types";

import { SEO_SIFTED_ON_PINK_IMAGE } from "lib/constants/shared";
import { fetchNavbarAndFooter } from "lib/fetchers/common";
import { fetchAboutPageData } from "content-ui/utilityPages/about/fetcher";

import AboutView from "content-ui/utilityPages/about";

export async function getStaticProps({ preview = false }) {
  const [menuItems, dato] = await Promise.all([
    fetchNavbarAndFooter(),
    fetchAboutPageData({ preview }),
  ]);

  return {
    props: {
      __initialState: {
        sitewideOption: { ...menuItems },
      },
      dato,
    },
    // we will attempt to re-generate the page:
    // - when a request comes in
    // - at most once every minute
    revalidate: 60,
  };
}

type PageProps = {
  dato: AboutPageData;
};

const AboutPage = (props: PageProps) => {
  const { seo } = props.dato.aboutPage;
  const title = seo?.title || "About us | Sifted";
  const description = seo?.description || "";
  const seoImage = seo?.image?.url || SEO_SIFTED_ON_PINK_IMAGE;

  return (
    <>
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: buildCanonicalUrl("/about"),
          openGraph: {
            title: title,
            description: description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Navigation />
      <ErrorBoundary>
        <AboutView data={props.dato.aboutPage} />
      </ErrorBoundary>
      <Footer />
    </>
  );
};

export default AboutPage;
